import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { useNavigate } from "react-router-dom";
import {
  getNetworkFacilityDetail,
  type NetworkFacilityDetailTab,
  type NetworkFacilityHeader,
  type NetworkFacilityRiskProfile,
} from "@services/networkFacilityDetail";
import { formatNetworkCurrency } from "@services/networkDashboardFormat";
import {
  formatPenaltyCitationCodes,
  formatPenaltyKind,
  formatQualityMeasureRows,
  formatStaffingMetric,
  isOptionalDetailTabVisible,
  ownershipTabHasUsefulData,
  staffingTabHasData,
} from "@utils/networkFacilityDetailFormat";
import type { NetworkSubTabId } from "../NetworkTab";
import { DASHBOARD, getDashboardWhiteCardStyles } from "../dashboard/dashboardTheme";
import NetworkFlagDot from "./NetworkFlagDot";

const DETAIL_TABS: { id: NetworkFacilityDetailTab; label: string; icon: React.ReactNode }[] = [
  { id: "risk-profile", label: "Risk Profile", icon: <FlagOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "survey", label: "Survey & Citations", icon: <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "staffing", label: "Staffing", icon: <GroupsOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "quality", label: "Quality Measures", icon: <FavoriteBorderOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "penalties-vbp", label: "Penalties & VBP", icon: <AttachMoneyOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "ownership", label: "Ownership", icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 16 }} /> },
];

const toneColor = (tone?: string) => {
  if (tone === "bad" || tone === "alert" || tone === "red") return "#b85c5c";
  if (tone === "warn" || tone === "amber") return "#c89234";
  if (tone === "good" || tone === "ok") return "#5d8f7a";
  return DASHBOARD.text;
};

const toneBg = (tone?: string) => {
  if (tone === "bad" || tone === "alert" || tone === "red") return "#f1d8d8";
  if (tone === "warn" || tone === "amber") return "#f5e6c5";
  if (tone === "good" || tone === "ok") return "#dfeae3";
  return DASHBOARD.iconButtonBg;
};

function PanelCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={(theme) => ({ ...getDashboardWhiteCardStyles(theme), mb: 2 })}>
      <Typography sx={{ fontWeight: 700, fontSize: "15px", color: DASHBOARD.header }}>{title}</Typography>
      {subtitle ? (
        <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted, mt: 0.5 }}>{subtitle}</Typography>
      ) : null}
      <Box sx={{ mt: 2 }}>{children}</Box>
    </Box>
  );
}

function FacilityHeaderBar({
  header,
  networkName,
  onBack,
  onPrev,
  onNext,
}: {
  header: NetworkFacilityHeader | null;
  networkName?: string;
  onBack: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  const locality = [header?.city, header?.state].filter(Boolean).join(", ");
  const rank = header?.network_rank;
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ textTransform: "none", color: DASHBOARD.accent, fontSize: "12px", minWidth: 0, px: 0 }}
        >
          {networkName ? `${networkName} · Network` : "Back to network"}
        </Button>
        <Typography sx={{ color: DASHBOARD.textMuted, fontSize: "12px" }}>/</Typography>
        <Typography sx={{ color: DASHBOARD.header, fontSize: "12px", fontWeight: 600 }}>
          {header?.provider_name ?? header?.ccn ?? "Facility"}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gap: 2,
          alignItems: "flex-end",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "22px", fontWeight: 700, color: DASHBOARD.header, lineHeight: 1.2 }}>
            {header?.provider_name ?? "Facility detail"}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: DASHBOARD.text, mt: 0.5 }}>
            CCN {header?.ccn}
            {locality ? ` · ${locality}` : ""}
            {header?.beds ? ` · ${header.beds} beds` : ""}
            {header?.ownership_type ? ` · ${header.ownership_type}` : ""}
          </Typography>
          {rank ? (
            <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted, mt: 0.5 }}>
              Network rank {rank.rank} of {rank.total} by composite risk
              {header?.as_of_date ? ` · CMS data as of ${header.as_of_date}` : ""}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", gap: 1, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
          <Button
            size="small"
            disabled={!header?.pager?.prev}
            onClick={onPrev}
            startIcon={<ChevronLeftIcon />}
            sx={{ textTransform: "none", color: DASHBOARD.accent }}
          >
            Prev
          </Button>
          <Button
            size="small"
            disabled={!header?.pager?.next}
            onClick={onNext}
            endIcon={<ChevronRightIcon />}
            sx={{ textTransform: "none", color: DASHBOARD.accent }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </>
  );
}

function RiskProfileTab({ data }: { data: NetworkFacilityRiskProfile }) {
  const hero = data.risk_hero;
  const score = hero?.score ?? data.header?.risk_score;
  const band = hero?.band ?? data.header?.risk_band;
  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "280px 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <Box
          sx={(theme) => ({
            ...getDashboardWhiteCardStyles(theme),
            textAlign: "center",
            py: 3,
          })}
        >
          <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted, mb: 1 }}>Composite risk score</Typography>
          <Typography sx={{ fontSize: "48px", fontWeight: 700, color: toneColor(band?.tone), lineHeight: 1 }}>
            {score ?? "—"}
          </Typography>
          <Chip
            label={band?.label ?? "Unknown"}
            size="small"
            sx={{ mt: 1.5, bgcolor: toneBg(band?.tone), color: toneColor(band?.tone), fontWeight: 600 }}
          />
          {hero?.network_rank ? (
            <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted, mt: 1.5 }}>
              Rank {hero.network_rank.rank} of {hero.network_rank.total} in portfolio
            </Typography>
          ) : null}
          <Typography sx={{ fontSize: "11px", color: DASHBOARD.textMuted, mt: 2, px: 1 }}>
            Factor bars use approximate CMS-derived components until full §13 ETL is available.
          </Typography>
        </Box>

        <PanelCard title="Weighted risk factors" subtitle="Domain contribution to composite score">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {(hero?.factors ?? []).map((f) => (
              <Box key={f.key}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: "13px", color: DASHBOARD.header }}>{f.label}</Typography>
                  <Typography sx={{ fontSize: "12px", color: toneColor(f.tone), fontWeight: 600 }}>
                    {f.points}/{f.max_points} pts
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (f.points / Math.max(f.max_points, 1)) * 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: DASHBOARD.borderSubtle,
                    "& .MuiLinearProgress-bar": { bgcolor: toneColor(f.tone), borderRadius: 4 },
                  }}
                />
              </Box>
            ))}
          </Box>
        </PanelCard>
      </Box>

      {(hero?.chain_comparison ?? []).length > 0 ? (
        <PanelCard title="Chain comparison" subtitle="Portfolio rank on key CMS metrics">
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
            {hero!.chain_comparison.map((row) => (
              <Box
                key={row.key}
                sx={{
                  border: `1px solid ${DASHBOARD.border}`,
                  borderRadius: "8px",
                  p: 1.5,
                  bgcolor: toneBg(row.tone),
                }}
              >
                <Typography sx={{ fontSize: "11px", color: DASHBOARD.textMuted }}>{row.label}</Typography>
                <Typography sx={{ fontSize: "18px", fontWeight: 700, color: DASHBOARD.header }}>
                  {row.value == null ? "—" : String(row.value)}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: toneColor(row.tone) }}>
                  #{row.rank} of {row.total}
                </Typography>
              </Box>
            ))}
          </Box>
        </PanelCard>
      ) : null}

      {(data.flag_cards ?? []).length > 0 ? (
        <PanelCard title="Active flags" subtitle="Enforcement and governance signals">
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            {data.flag_cards.map((card) => {
              const title = (card as { title?: string }).title ?? card.label ?? card.key;
              return (
                <Box
                  key={card.key}
                  sx={{
                    border: `1px solid ${DASHBOARD.border}`,
                    borderRadius: "8px",
                    p: 1.5,
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                  }}
                >
                  <NetworkFlagDot cell={{ status: (card.status as "ok" | "warn" | "alert" | "neutral") ?? "neutral", value: card.value }} />
                  <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: DASHBOARD.header }}>{title}</Typography>
                    {card.value != null ? (
                      <Typography sx={{ fontSize: "12px", color: DASHBOARD.text }}>{String(card.value)}</Typography>
                    ) : null}
                    {card.detail ? (
                      <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted, mt: 0.5 }}>{card.detail}</Typography>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </PanelCard>
      ) : null}
    </>
  );
}

function GenericRecordTable({
  rows,
  columns,
  columnLabels,
}: {
  rows: Record<string, unknown>[];
  columns: string[];
  columnLabels?: Record<string, string>;
}) {
  if (!rows.length) {
    return <Typography sx={{ fontSize: "13px", color: DASHBOARD.textMuted }}>No records for this facility.</Typography>;
  }
  return (
    <Table size="small" sx={{ "& td, & th": { fontSize: "12px" } }}>
      <TableHead>
        <TableRow>
          {columns.map((c) => (
            <TableCell key={c}>{columnLabels?.[c] ?? c.replace(/_/g, " ")}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {columns.map((c) => (
              <TableCell key={c}>{row[c] == null ? "—" : String(row[c])}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SurveyTab({ data }: { data: Record<string, unknown> }) {
  const survey = (data.survey ?? data) as Record<string, unknown>;
  const rawCitations = survey.citations ?? survey.facilities ?? [];
  const rows = Array.isArray(rawCitations) ? (rawCitations as Record<string, unknown>[]) : [];
  const profile = (survey.citation_profile ?? survey.summary ?? {}) as Record<string, unknown>;
  return (
    <>
      {Object.keys(profile).length > 0 ? (
        <PanelCard title="Citation profile">
          <Typography sx={{ fontSize: "13px", color: DASHBOARD.text }}>
            {Object.entries(profile)
              .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
              .join(" · ")}
          </Typography>
        </PanelCard>
      ) : null}
      <PanelCard title="Citations" subtitle="Deficiency events for this CCN">
        <GenericRecordTable
          rows={rows.slice(0, 100)}
          columns={["citation_code", "tag_code", "severity", "event_date", "cycle"]}
        />
      </PanelCard>
    </>
  );
}

const STAFFING_METRICS: { key: string; label: string }[] = [
  { key: "adjusted_total_nurse_hprd", label: "Adjusted total nurse HPRD" },
  { key: "state_avg_total_hprd", label: "State avg total HPRD" },
  { key: "gap_vs_state_hprd", label: "Gap vs state HPRD" },
  { key: "adjusted_rn_hprd", label: "Adjusted RN HPRD" },
  { key: "state_avg_rn_hprd", label: "State avg RN HPRD" },
  { key: "gap_rn_hprd", label: "RN gap vs state" },
  { key: "total_nursing_turnover", label: "Total nursing turnover" },
  { key: "rn_turnover", label: "RN turnover" },
  { key: "administrator_turnover_count", label: "Administrator turnover" },
];

function StaffingTab({ data }: { data: Record<string, unknown> }) {
  const staffing = (data.staffing ?? data) as Record<string, unknown>;
  const visibleMetrics = STAFFING_METRICS.filter((metric) => staffing[metric.key] != null);
  return (
    <PanelCard title="Staffing & workforce">
      {visibleMetrics.length === 0 ? (
        <Typography sx={{ fontSize: "13px", color: DASHBOARD.textMuted }}>
          No staffing metrics for this reporting period.
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 1.5 }}>
          {visibleMetrics.map((metric) => (
            <Box key={metric.key} sx={{ border: `1px solid ${DASHBOARD.border}`, borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: "11px", color: DASHBOARD.textMuted }}>{metric.label}</Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: DASHBOARD.header }}>
                {formatStaffingMetric(staffing[metric.key])}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {(data.network_averages as Record<string, unknown> | undefined) ? (
        <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted, mt: 2 }}>
          Network averages available for benchmark comparison in portfolio view.
        </Typography>
      ) : null}
    </PanelCard>
  );
}

function QualityTab({ data }: { data: Record<string, unknown> }) {
  const topLevelMeasures = Array.isArray(data.measures) ? (data.measures as Record<string, unknown>[]) : [];
  const quality = (data.quality ?? data) as Record<string, unknown>;
  const nestedMeasures = Array.isArray(quality.measures) ? (quality.measures as Record<string, unknown>[]) : [];
  const cellMeasures = Array.isArray(quality.cells) ? (quality.cells as Record<string, unknown>[]) : [];
  const measures =
    topLevelMeasures.length > 0
      ? topLevelMeasures
      : nestedMeasures.length > 0
        ? nestedMeasures
        : cellMeasures.map((cell) => ({
            measure_code: cell.measure_code,
            measure_name: cell.measure_code,
            score: cell.score,
            flag: cell.quintile != null && Number(cell.quintile) >= 4 ? "High harm" : null,
          }));
  const rows = formatQualityMeasureRows(measures);
  const flagRows = rows.filter((row) => row.flag && row.flag !== "—");
  const columns = flagRows.length > 0
    ? ["measure_code", "measure_name", "score", "flag"]
    : ["measure_code", "measure_name", "score"];
  return (
    <PanelCard title="Clinical quality measures" subtitle="Four-quarter average scores where available">
      {rows.length > 0 ? (
        <GenericRecordTable
          rows={rows}
          columns={columns}
          columnLabels={{
            measure_code: "Code",
            measure_name: "Measure",
            score: "Score",
            flag: "Flag",
          }}
        />
      ) : (
        <Typography sx={{ fontSize: "13px", color: DASHBOARD.textMuted }}>No quality measures for this facility.</Typography>
      )}
    </PanelCard>
  );
}

function PenaltiesVbpTab({ data }: { data: Record<string, unknown> }) {
  const penalties = (data.penalties ?? {}) as { total_36mo?: number; entries?: Record<string, unknown>[] };
  const vbp = (data.vbp ?? {}) as Record<string, unknown>;
  const penaltyRows = (penalties.entries ?? []).slice(0, 20).map((entry) => {
    const amount = entry.amount;
    const codes = formatPenaltyCitationCodes(entry.citation_code);
    return {
      date: entry.date,
      kind: formatPenaltyKind(entry.kind as string | undefined),
      amount:
        amount == null || entry.kind === "payment_denial"
          ? "—"
          : formatNetworkCurrency(Number(amount)),
      citation_code: codes.length > 0 ? codes.join(", ") : "—",
    };
  });
  return (
    <>
      <PanelCard title="Penalties (36 mo)" subtitle={`Total ${formatNetworkCurrency(Number(penalties.total_36mo ?? 0))}`}>
        {penaltyRows.length > 0 ? (
          <GenericRecordTable
            rows={penaltyRows}
            columns={["date", "kind", "amount", "citation_code"]}
            columnLabels={{
              date: "Date",
              kind: "Type",
              amount: "Amount",
              citation_code: "Citation codes",
            }}
          />
        ) : (
          <Typography sx={{ fontSize: "13px", color: DASHBOARD.textMuted }}>No penalties in the last 36 months.</Typography>
        )}
      </PanelCard>
      <PanelCard title="SNF VBP performance">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 1.5 }}>
          {["performance_score", "incentive_payment_multiplier", "estimated_payment_adjustment"].map((key) =>
            vbp[key] != null ? (
              <Box key={key} sx={{ border: `1px solid ${DASHBOARD.border}`, borderRadius: "8px", p: 1.5 }}>
                <Typography sx={{ fontSize: "11px", color: DASHBOARD.textMuted }}>{key.replace(/_/g, " ")}</Typography>
                <Typography sx={{ fontSize: "18px", fontWeight: 700, color: DASHBOARD.header }}>{String(vbp[key])}</Typography>
              </Box>
            ) : null,
          )}
        </Box>
      </PanelCard>
    </>
  );
}

function OwnershipTab({ data }: { data: Record<string, unknown> }) {
  const ownership = (data.ownership ?? data) as { entries?: Record<string, unknown>[]; admin_turnover_count?: number };
  return (
    <>
      {ownership.admin_turnover_count != null ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Administrator turnover count: {ownership.admin_turnover_count}. Named administrator timelines are not available from CMS open-data.
        </Alert>
      ) : null}
      <PanelCard title="Ownership roles">
        <GenericRecordTable
          rows={ownership.entries ?? []}
          columns={["owner_name", "role", "owner_type", "ownership_percentage", "association_date"]}
          columnLabels={{
            owner_name: "Owner",
            role: "Role",
            owner_type: "Type",
            ownership_percentage: "Ownership %",
            association_date: "Association date",
          }}
        />
      </PanelCard>
    </>
  );
}

export interface NetworkFacilityDetailPageProps {
  ccn: string;
  networkId?: number;
  facilityId?: number;
  networkName?: string;
  networkSubTab?: NetworkSubTabId;
}

export default function NetworkFacilityDetailPage({
  ccn,
  networkId,
  facilityId,
  networkName,
  networkSubTab = "flags",
}: NetworkFacilityDetailPageProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<NetworkFacilityDetailTab>("risk-profile");
  const [header, setHeader] = useState<NetworkFacilityHeader | null>(null);
  const [tabData, setTabData] = useState<Record<string, unknown> | null>(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tabDataCacheRef = useRef<Partial<Record<NetworkFacilityDetailTab, Record<string, unknown>>>>({});
  const loadedTabsRef = useRef<Set<NetworkFacilityDetailTab>>(new Set());
  const abortRef = useRef<AbortController | null>(null);
  const activeTabRef = useRef<NetworkFacilityDetailTab>("risk-profile");
  activeTabRef.current = activeTab;
  const [staffingTabResolved, setStaffingTabResolved] = useState(false);
  const [staffingTabEnabled, setStaffingTabEnabled] = useState(false);
  const [ownershipTabResolved, setOwnershipTabResolved] = useState(false);
  const [ownershipTabEnabled, setOwnershipTabEnabled] = useState(false);
  const root = useMemo(
    () => (networkId != null ? { networkId } : { facilityId: facilityId! }),
    [networkId, facilityId],
  );

  const loadTab = useCallback(
    async (tab: NetworkFacilityDetailTab, targetCcn: string, signal?: AbortSignal) => {
      if (loadedTabsRef.current.has(tab) && tabDataCacheRef.current[tab]) {
        if (activeTabRef.current === tab) {
          setTabData(tabDataCacheRef.current[tab]!);
        }
        return;
      }
      const hasCached = tabDataCacheRef.current[tab] != null;
      if (!hasCached) {
        setTabLoading(true);
      }
      setError(null);
      try {
        const res = await getNetworkFacilityDetail(targetCcn, root, tab, { signal });
        if (signal?.aborted) return;
        const payload = res.data as Record<string, unknown> & { header?: NetworkFacilityHeader };
        if (payload.header) setHeader(payload.header);
        tabDataCacheRef.current[tab] = payload;
        loadedTabsRef.current.add(tab);
        if (tab === "staffing") {
          setStaffingTabResolved(true);
          setStaffingTabEnabled(staffingTabHasData(payload));
        }
        if (tab === "ownership") {
          setOwnershipTabResolved(true);
          setOwnershipTabEnabled(ownershipTabHasUsefulData(payload));
        }
        if (activeTabRef.current === tab) {
          setTabData(payload);
        }
      } catch (e) {
        if (signal?.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to load facility detail");
      } finally {
        if (!signal?.aborted) setTabLoading(false);
      }
    },
    [root],
  );

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    tabDataCacheRef.current = {};
    loadedTabsRef.current = new Set();
    setStaffingTabResolved(false);
    setStaffingTabEnabled(false);
    setOwnershipTabResolved(false);
    setOwnershipTabEnabled(false);
    setHeader(null);
    setTabData(null);
    setTabLoading(true);
    setError(null);
    setActiveTab("risk-profile");

    const loadInitial = async () => {
      await loadTab("risk-profile", ccn, controller.signal);
      if (!controller.signal.aborted) {
        void loadTab("staffing", ccn);
        void loadTab("ownership", ccn);
      }
    };

    void loadInitial();
    return () => controller.abort();
  }, [ccn, loadTab, root]);

  useEffect(() => {
    const cached = tabDataCacheRef.current[activeTab];
    if (cached) {
      setTabData(cached);
      return;
    }
    if (activeTab === "risk-profile") return;
    void loadTab(activeTab, ccn);
  }, [activeTab, ccn, loadTab]);

  const visibleTabs = useMemo(() => {
    return DETAIL_TABS.filter((tab) => {
      if (tab.id === "staffing") {
        return isOptionalDetailTabVisible(staffingTabResolved, staffingTabEnabled);
      }
      if (tab.id === "ownership") {
        return isOptionalDetailTabVisible(ownershipTabResolved, ownershipTabEnabled);
      }
      return true;
    });
  }, [staffingTabResolved, staffingTabEnabled, ownershipTabResolved, ownershipTabEnabled]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab("risk-profile");
      const cached = tabDataCacheRef.current["risk-profile"];
      if (cached) setTabData(cached);
    }
  }, [visibleTabs, activeTab]);

  const goBack = () => {
    navigate("/dashboard/facilities", {
      state: {
        currentView: "network",
        ...(networkId != null ? { networkId } : {}),
        networkSubTab,
      },
    });
  };

  const goToCcn = (nextCcn: string) => {
    navigate("/dashboard/facilities", {
      state: {
        currentView: "network-facility",
        ccn: nextCcn,
        networkId,
        facilityId,
        networkSubTab,
        networkName,
      },
      replace: true,
    });
  };

  const renderTab = () => {
    if (!tabData) return null;
    if (activeTab === "risk-profile") return <RiskProfileTab data={tabData as unknown as NetworkFacilityRiskProfile} />;
    if (activeTab === "survey") return <SurveyTab data={tabData} />;
    if (activeTab === "staffing") return <StaffingTab data={tabData} />;
    if (activeTab === "quality") return <QualityTab data={tabData} />;
    if (activeTab === "penalties-vbp") return <PenaltiesVbpTab data={tabData} />;
    if (activeTab === "ownership") return <OwnershipTab data={tabData} />;
    return null;
  };

  return (
    <Box
      sx={{
        fontFamily: "Rethink Sans, sans-serif",
        width: "100%",
        boxSizing: "border-box",
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <FacilityHeaderBar
        header={header}
        networkName={networkName}
        onBack={goBack}
        onPrev={header?.pager?.prev ? () => goToCcn(header.pager!.prev!.ccn) : undefined}
        onNext={header?.pager?.next ? () => goToCcn(header.pager!.next!.ccn) : undefined}
      />

      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          overflowX: "auto",
          gap: 0.5,
          bgcolor: "#fff",
          border: `1px solid ${DASHBOARD.border}`,
          borderRadius: "12px",
          p: 0.5,
          mb: 2.75,
        }}
      >
        {visibleTabs.map((t) => (
          <Button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            sx={{
              textTransform: "none",
              fontSize: "13px",
              fontWeight: activeTab === t.id ? 600 : 500,
              borderRadius: "8px",
              color: activeTab === t.id ? "#fff" : DASHBOARD.text,
              px: 2,
              py: 1.125,
              minHeight: 36,
              whiteSpace: "nowrap",
              bgcolor: activeTab === t.id ? DASHBOARD.accent : "transparent",
              flexShrink: 0,
              "&:hover": {
                bgcolor: activeTab === t.id ? DASHBOARD.accent : DASHBOARD.tintedCardBg,
              },
              "& .MuiSvgIcon-root": {
                color: activeTab === t.id ? "#fff" : DASHBOARD.text,
              },
            }}
          >
            {t.icon}
            <Box component="span" sx={{ ml: 1 }}>{t.label}</Box>
          </Button>
        ))}
      </Box>

      <Box
        sx={(theme) => ({
          ...getDashboardWhiteCardStyles(theme),
          minHeight: 200,
        })}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {tabLoading && !tabData ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: DASHBOARD.accent }} />
          </Box>
        ) : tabLoading && !tabDataCacheRef.current[activeTab] ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: DASHBOARD.accent }} />
          </Box>
        ) : (
          renderTab()
        )}
      </Box>
    </Box>
  );
}
